import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getClient, getClientDevices, getClientTickets,
  analyzeClient, getContacts, updateDevice,
  sendClientWelcomeEmails,
} from "../api";
import RemoteButton from "../components/RemoteButton";
import { tokens, sx, FullPageSpinner } from "../theme";
import useIsMobile from "../hooks/useIsMobile";

const API   = "/api";
const token = () => localStorage.getItem("midtc_token") || "";

// ── Safe normalizers ───────────────────────────────────────────────────────
const normalizeObj   = (r) => r?.data ?? r ?? null;
const normalizeArray = (r) => Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];

// ── Helpers ────────────────────────────────────────────────────────────────
const scoreColor    = (s) => s >= 80 ? "#22c55e" : s >= 60 ? "#f59e0b" : "#ef4444";
const scoreLabel    = (s) => s >= 80 ? "Healthy"  : s >= 60 ? "Warning"  : "Critical";
const slaColor      = (s) => ({ ok:"#22c55e", breach:"#ef4444", hold:"#f59e0b" }[s] || "#6b7280");
const slaLabel      = (s) => ({ ok:"Within SLA", breach:"SLA Breached", hold:"SLA Hold" }[s] || s);
const statusColor   = (s) => ({ Open:"#3b82f6","In Progress":"#8b5cf6","On Hold":"#f59e0b",Resolved:"#22c55e",Closed:"#6b7280" }[s] || "#6b7280");
const priorityColor = (p) => ({ Critical:"#ef4444",High:"#f97316",Medium:"#f59e0b",Low:"#22c55e" }[p] || "#6b7280");
const fmt     = (d) => d ? new Date(d).toLocaleDateString() : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleString()     : "Never";

// ── Styles (sourced from theme) ────────────────────────────────────────────
const S = {
  page:    { minHeight:"100vh", background:`radial-gradient(circle at 20% -10%, rgba(99,102,241,0.05), transparent 50%), ${tokens.surface.page}`, color:tokens.text.primary, padding:"24px", fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  back:    { background:"none", border:"none", color:tokens.text.faint, cursor:"pointer", fontSize:13, marginBottom:14, display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" },
  header:  { paddingBottom:14, borderBottom:`1px solid ${tokens.border.soft}`, marginBottom:18, display:"flex", justifyContent:"space-between", alignItems:"flex-end", flexWrap:"wrap", gap:12 },
  title:   { fontSize:22, fontWeight:800, color:tokens.text.primary, margin:0, letterSpacing:"-0.5px" },
  sub:     { fontSize:13, color:tokens.text.faint, marginTop:3 },
  grid:    { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:10, marginBottom:18 },
  stat:    { background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:"15px 16px", position:"relative", overflow:"hidden", boxShadow:tokens.shadow.panel, transition:"all 0.18s" },
  statVal: { fontSize:28, fontWeight:800, color:tokens.text.primary, letterSpacing:-1, lineHeight:1 },
  statLbl: { fontSize:10, color:tokens.text.label, marginTop:6, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" },
  card:    { background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:20, marginBottom:14, boxShadow:tokens.shadow.panel },
  cardH:   { fontSize:13, fontWeight:700, color:tokens.text.body, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center" },
  table:   { width:"100%", borderCollapse:"collapse" },
  th:      { textAlign:"left", padding:"10px 12px", fontSize:10, color:tokens.text.faint, borderBottom:`1px solid ${tokens.border.soft}`, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" },
  td:      { padding:"11px 12px", fontSize:13, color:tokens.text.body, borderBottom:`1px solid ${tokens.border.softer}` },
  badge:   (c) => ({ display:"inline-flex", alignItems:"center", padding:"3px 9px", borderRadius:99, fontSize:11, fontWeight:700, background:c+"22", color:c, border:`1px solid ${c}40` }),
  btn:     (c) => c
    ? { background:c, color:"#fff", border:"none", borderRadius:7, padding:"7px 14px", fontSize:12, cursor:"pointer", fontWeight:700, fontFamily:"inherit" }
    : { ...sx.buttonPrimary, padding:"7px 14px", fontSize:12 },
  score:   (s) => ({ display:"inline-flex", alignItems:"center", justifyContent:"center", width:42, height:42, borderRadius:"50%", fontSize:14, fontWeight:800, background:scoreColor(s)+"22", color:scoreColor(s), border:`1px solid ${scoreColor(s)}40`, boxShadow:`0 0 12px ${scoreColor(s)}33` }),
  alert:   { background:"rgba(127,29,29,0.3)", border:`1px solid rgba(239,68,68,0.4)`, borderRadius:8, padding:"7px 11px", fontSize:12, color:tokens.accent.red2, marginBottom:6 },
  ai:      { background:"linear-gradient(135deg,rgba(124,58,237,0.12),rgba(99,102,241,0.06))", border:"1px solid rgba(124,58,237,0.3)", borderRadius:tokens.radius.md, padding:16, fontSize:13, color:"var(--t-text-body)", lineHeight:1.7, whiteSpace:"pre-wrap", boxShadow:"0 0 18px rgba(124,58,237,0.1)" },
  spinner: { width:20, height:20, border:`2px solid ${tokens.accent.indigo}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", display:"inline-block" },
  section:      { background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:20, marginBottom:14, boxShadow:tokens.shadow.panel },
  sectionTitle: { fontSize:11, fontWeight:700, color:tokens.text.label, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14, display:"flex", alignItems:"center", gap:8 },
  sinput:       { ...sx.input, padding:"9px 12px", fontSize:14 },
  slabel:       { fontSize:11, color:tokens.text.label, display:"block", marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" },
  btnBlue:      { ...sx.buttonPrimary, padding:"9px 18px" },
  btnRed:       { background:"rgba(220,38,38,0.15)", color:tokens.accent.red, border:"1px solid rgba(220,38,38,0.35)", padding:"9px 18px", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6, fontFamily:"inherit" },
  btnGreen:     { background:"rgba(22,163,74,0.15)", color:tokens.accent.green, border:"1px solid rgba(22,163,74,0.35)", padding:"9px 18px", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6, fontFamily:"inherit" },
  btnGhost:     { ...sx.buttonGhost, padding:"9px 18px", fontWeight:600 },
  btnSm:        { background:"rgba(99,102,241,0.12)", color:tokens.accent.indigo2, border:"1px solid rgba(99,102,241,0.3)", padding:"6px 12px", borderRadius:7, fontSize:12, cursor:"pointer", fontWeight:700, fontFamily:"inherit" },
  sbadge:       (c) => ({ background:c+"22", color:c, border:`1px solid ${c}44`, borderRadius:999, padding:"3px 10px", fontSize:11, fontWeight:700 }),
  row2:         { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 },
};

// ── M365 Logo ──────────────────────────────────────────────────────────────
const M365Logo = ({ size=14 }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" style={{ flexShrink:0, verticalAlign:"middle" }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
//  SHARED SETTINGS HELPERS
// ═══════════════════════════════════════════════════════════════════════════
function ResultBanner({ result, onDismiss }) {
  if (!result) return null;
  const ok = result.type === "success";
  return (
    <div style={{ background:ok?"rgba(22,163,74,0.1)":"rgba(220,38,38,0.1)", border:`1px solid ${ok?"rgba(22,163,74,0.3)":"rgba(220,38,38,0.3)"}`, borderRadius:8, padding:"14px 16px", marginTop:14, display:"flex", alignItems:"flex-start", gap:10 }}>
      <span style={{ fontSize:18 }}>{ok?"✅":"❌"}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, color:ok?"#4ade80":"#f87171", fontSize:14 }}>{ok?"Operation Completed":"Operation Failed"}</div>
        <div style={{ color:"var(--t-text-muted)", fontSize:13, marginTop:3, lineHeight:1.5 }}>{result.message}</div>
        {result.details && (
          <div style={{ background: "var(--t-panel)", border:"1px solid var(--t-border-strong)", borderRadius:6, padding:"8px 10px", marginTop:8, fontFamily:"monospace", fontSize:12, color:"var(--t-text-label)", whiteSpace:"pre-wrap" }}>
            {result.details}
          </div>
        )}
      </div>
      <button onClick={onDismiss} style={{ background:"none", border:"none", color:"var(--t-text-deep)", cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>×</button>
    </div>
  );
}

function ConfirmDialog({ message, subtext, onConfirm, onCancel, confirmLabel="Confirm", danger=false }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:12, padding:28, maxWidth:420, width:"100%", boxShadow:"0 24px 64px rgba(0,0,0,0.5)" }}>
        <div style={{ fontSize:32, marginBottom:12, textAlign:"center" }}>{danger?"⚠️":"❓"}</div>
        <h3 style={{ margin:"0 0 8px", color:"var(--t-text-primary)", fontSize:16, textAlign:"center" }}>{message}</h3>
        {subtext && <p style={{ color:"var(--t-text-label)", fontSize:13, textAlign:"center", margin:"0 0 20px", lineHeight:1.5 }}>{subtext}</p>}
        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          <button onClick={onCancel}  style={{ ...S.btnGhost, flex:1 }}>Cancel</button>
          <button onClick={onConfirm} style={{ ...(danger?S.btnRed:S.btnBlue), flex:1, justifyContent:"center" }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  CLIENT SETTINGS TAB
// ═══════════════════════════════════════════════════════════════════════════
function ClientSettingsTab({ client, onUpdate }) {
  const isMobile = useIsMobile();
  // ── Basic Info ──────────────────────────────────────────────────────────
  const [info, setInfo] = useState({
    company_name: client.company_name || "",
    phone:        client.phone        || "",
    address:      client.address      || "",
    city:         client.city         || "",
    state:        client.state        || "",
    zip:          client.zip          || "",
    notes:        client.notes        || "",
  });
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoResult, setInfoResult] = useState(null);

  // ── Domain (single field — clients table has one domain column) ─────────
  const [domain,       setDomain]       = useState(client.domain || "");
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainResult, setDomainResult] = useState(null);

  // ── O365 Connector — only azure_tenant_id is stored per-client in DB ────
  // azure_client_id and azure_client_secret come from global env vars on the
  // backend (AZURE_CLIENT_ID / AZURE_CLIENT_SECRET). Only Tenant ID is per-client.
  const [tenantId,      setTenantId]      = useState(client.azure_tenant_id || "");
  const [tenantSaving,  setTenantSaving]  = useState(false);
  const [tenantResult,  setTenantResult]  = useState(null);

  // ── O365 Actions ────────────────────────────────────────────────────────
  const [actionResult,  setActionResult]  = useState(null);
  const [runningAction, setRunningAction] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // ── SLA Policy ──────────────────────────────────────────────────────────
  const [policies,    setPolicies]    = useState([]);
  const [slaPolicyId, setSlaPolicyId] = useState(client.sla_policy_id || "");
  const [slaSaving,   setSlaSaving]   = useState(false);
  const [slaResult,   setSlaResult]   = useState(null);

  // ── Load SLA policies on mount ───────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/sla-policies`, { headers:{ Authorization:`Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => setPolicies(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const saveInfo = async () => {
    setInfoSaving(true); setInfoResult(null);
    try {
      const res  = await fetch(`${API}/clients/${client.id}`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify(info),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save.");
      setInfoResult({ type:"success", message:"Client information saved successfully." });
      if (onUpdate) onUpdate();
    } catch (err) { setInfoResult({ type:"error", message:err.message }); }
    finally { setInfoSaving(false); }
  };

  const saveDomain = async () => {
    setDomainSaving(true); setDomainResult(null);
    try {
      const res  = await fetch(`${API}/clients/${client.id}`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify({ domain: domain.trim().toLowerCase() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save domain.");
      setDomainResult({ type:"success", message:`Primary domain set to ${domain.trim()}.` });
      if (onUpdate) onUpdate();
    } catch (err) { setDomainResult({ type:"error", message:err.message }); }
    finally { setDomainSaving(false); }
  };

  // Saves Azure Tenant ID using the existing PATCH /api/clients/:id/azure-tenant route
  const saveTenantId = async () => {
    setTenantSaving(true); setTenantResult(null);
    try {
      const res  = await fetch(`${API}/clients/${client.id}/azure-tenant`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify({ azure_tenant_id: tenantId.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save Tenant ID.");
      setTenantResult({ type:"success", message:"Azure Tenant ID saved. O365 password resets are now enabled for this client's contacts." });
      if (onUpdate) onUpdate();
    } catch (err) { setTenantResult({ type:"error", message:err.message }); }
    finally { setTenantSaving(false); }
  };

  const runO365Action = async (action) => {
    setConfirmAction(null);
    setRunningAction(action.key); setActionResult(null);
    try {
      const res  = await fetch(`${API}/clients/${client.id}/o365-actions/${action.key}`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `${action.label} failed.`);
      setActionResult({
        type:"success",
        message:`${action.label} completed successfully.`,
        details: data.summary || data.message || JSON.stringify(data, null, 2),
      });
    } catch (err) {
      setActionResult({ type:"error", message:err.message });
    } finally { setRunningAction(null); }
  };

  const saveSLA = async () => {
    setSlaSaving(true); setSlaResult(null);
    try {
      const res  = await fetch(`${API}/clients/${client.id}`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify({ sla_policy_id: slaPolicyId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save SLA policy.");
      setSlaResult({ type:"success", message:"SLA policy updated." });
      if (onUpdate) onUpdate();
    } catch (err) { setSlaResult({ type:"error", message:err.message }); }
    finally { setSlaSaving(false); }
  };

  // ── O365 action definitions ───────────────────────────────────────────────
  const O365_ACTIONS = [
    { key:"sync-users",        label:"Sync Users",             icon:"🔄", description:"Pull all licensed users from M365 and create/update contacts in the system.",          danger:false },
    { key:"check-licenses",    label:"Check Licenses",         icon:"📋", description:"Retrieve current license counts and usage from this tenant.",                          danger:false },
    { key:"check-mfa",         label:"MFA Status Report",      icon:"🔐", description:"Generate a report of users with MFA enabled/disabled in this tenant.",                 danger:false },
    { key:"disabled-accounts", label:"List Disabled Accounts", icon:"🚫", description:"Show all disabled user accounts in this tenant.",                                      danger:false },
    { key:"stale-accounts",    label:"Stale Account Report",   icon:"🕰️", description:"List accounts that haven't signed in for over 90 days.",                              danger:false },
    { key:"revoke-sessions",   label:"Revoke All Sessions",    icon:"⚡", description:"Force sign-out all users in this tenant. Use during security incidents.",              danger:true  },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop:4 }}>

      {/* ── 1. CLIENT INFO ────────────────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>🏢 Client Information</div>
        <div style={S.row2}>
          <div style={{ gridColumn:"1/-1" }}>
            <label style={S.slabel}>Company Name</label>
            <input style={S.sinput} value={info.company_name} onChange={e => setInfo(p=>({...p,company_name:e.target.value}))} />
          </div>
          <div>
            <label style={S.slabel}>Phone</label>
            <input style={S.sinput} value={info.phone} onChange={e => setInfo(p=>({...p,phone:e.target.value}))} />
          </div>
          <div>
            <label style={S.slabel}>Address</label>
            <input style={S.sinput} value={info.address} onChange={e => setInfo(p=>({...p,address:e.target.value}))} />
          </div>
          <div>
            <label style={S.slabel}>City</label>
            <input style={S.sinput} value={info.city} onChange={e => setInfo(p=>({...p,city:e.target.value}))} />
          </div>
          <div>
            <label style={S.slabel}>State</label>
            <input style={S.sinput} value={info.state} onChange={e => setInfo(p=>({...p,state:e.target.value}))} />
          </div>
          <div>
            <label style={S.slabel}>ZIP</label>
            <input style={S.sinput} value={info.zip} onChange={e => setInfo(p=>({...p,zip:e.target.value}))} />
          </div>
          <div style={{ gridColumn:"1/-1" }}>
            <label style={S.slabel}>Internal Notes</label>
            <textarea style={{ ...S.sinput, resize:"vertical" }} rows={3} value={info.notes} onChange={e => setInfo(p=>({...p,notes:e.target.value}))} />
          </div>
        </div>
        <button onClick={saveInfo} disabled={infoSaving} style={S.btnBlue}>
          {infoSaving ? "Saving…" : "💾 Save Client Info"}
        </button>
        <ResultBanner result={infoResult} onDismiss={() => setInfoResult(null)} />
      </div>

      {/* ── 2. SLA POLICY ─────────────────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>⏱ SLA Policy</div>
        <label style={S.slabel}>Assigned SLA Policy</label>
        <select
          value={slaPolicyId}
          onChange={e => setSlaPolicyId(e.target.value)}
          style={{ ...S.sinput, cursor:"pointer", marginBottom:14 }}
        >
          <option value="">No SLA Policy</option>
          {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {slaPolicyId && policies.find(p=>p.id===slaPolicyId) && (() => {
          const pol = policies.find(p => p.id === slaPolicyId);
          return (
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)", gap:8, marginBottom:14 }}>
              {["Critical","High","Medium","Low"].map(lvl => (
                <div key={lvl} style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:7, padding:"8px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:"var(--t-text-label)", marginBottom:2 }}>{lvl}</div>
                  <div style={{ fontSize:14, fontWeight:700, color: lvl==="Critical"?"#ef4444":lvl==="High"?"#f97316":lvl==="Medium"?"#f59e0b":"#22c55e" }}>
                    {pol[`response_time_${lvl.toLowerCase()}`]}m
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
        <button onClick={saveSLA} disabled={slaSaving} style={S.btnBlue}>
          {slaSaving ? "Saving…" : "💾 Save SLA Policy"}
        </button>
        <ResultBanner result={slaResult} onDismiss={() => setSlaResult(null)} />
      </div>

      {/* ── 3. DOMAIN ─────────────────────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>🌐 Primary Domain</div>
        <p style={{ margin:"0 0 14px", fontSize:13, color:"var(--t-text-label)", lineHeight:1.6 }}>
          Used for email routing, M365 SSO matching, and auto-assigning inbound tickets from
          email to the correct client. Must match the client's email domain (e.g. <code style={{ color:"#60a5fa" }}>acmecorp.com</code>).
        </p>

        <div style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:13, color:"var(--t-text-label)" }}>Current:</span>
          <span style={{ fontSize:14, fontWeight:700, color:"var(--t-text-primary)" }}>{client.domain || "Not set"}</span>
          {client.domain
            ? <span style={S.sbadge("#4ade80")}>✓ Active</span>
            : <span style={S.sbadge("#f59e0b")}>⚠ Not configured</span>}
        </div>

        <div style={{ display:"flex", gap:8, flexWrap:isMobile?"wrap":"nowrap" }}>
          <input
            style={{ ...S.sinput, flex:isMobile?"1 1 100%":1 }}
            placeholder="example.com"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            onKeyDown={e => e.key==="Enter" && saveDomain()}
          />
          <button onClick={saveDomain} disabled={domainSaving || !domain.trim()} style={S.btnBlue}>
            {domainSaving ? "Saving…" : "💾 Save Domain"}
          </button>
        </div>
        <ResultBanner result={domainResult} onDismiss={() => setDomainResult(null)} />
      </div>

      {/* ── 4. MICROSOFT 365 CONNECTOR ───────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}><M365Logo /> Microsoft 365 Connector</div>
        <p style={{ margin:"0 0 14px", fontSize:13, color:"var(--t-text-label)", lineHeight:1.6 }}>
          The <strong style={{ color:"var(--t-text-muted)" }}>Azure Tenant ID</strong> is stored per-client and is required for
          Graph API operations including O365 password resets on this client's contacts.
          The App Registration credentials (Client ID &amp; Secret) are configured globally
          in the backend environment — contact your system administrator to update them.
        </p>

        {/* Required permissions callout */}
        <div style={{ background:"rgba(29,78,216,0.08)", border:"1px solid rgba(29,78,216,0.30)", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"var(--badge-blue)" }}>
          <div style={{ fontWeight:700, marginBottom:4 }}>Required Azure App Permissions (Application type)</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {["User.Read.All","Directory.Read.All","UserAuthenticationMethod.ReadWrite.All","AuditLog.Read.All","Reports.Read.All"].map(p => (
              <span key={p} style={{ background:"rgba(29,78,216,0.12)", border:"1px solid rgba(29,78,216,0.30)", borderRadius:4, padding:"2px 7px", fontFamily:"monospace", color:"var(--badge-blue)" }}>{p}</span>
            ))}
          </div>
        </div>

        <label style={S.slabel}>Azure Tenant ID</label>
        <div style={{ display:"flex", gap:8, marginBottom:4, flexWrap:isMobile?"wrap":"nowrap" }}>
          <input
            style={{ ...S.sinput, flex:isMobile?"1 1 100%":1, fontFamily:"monospace", fontSize:13 }}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
          />
          <button onClick={saveTenantId} disabled={tenantSaving} style={S.btnBlue}>
            {tenantSaving ? "Saving…" : "💾 Save"}
          </button>
        </div>
        <div style={{ fontSize:11, color:"var(--t-border-strong)", marginBottom:4 }}>
          Find this in Azure Portal → Microsoft Entra ID → Overview → Tenant ID
        </div>

        {tenantId && (
          <div style={{ marginTop:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", background:"rgba(22,163,74,0.1)", border:"1px solid rgba(22,163,74,0.35)", borderRadius:7, fontSize:12, color:"var(--badge-green)" }}>
              ✓ Tenant ID configured — O365 password resets enabled for this client's contacts
            </div>
          </div>
        )}
        <ResultBanner result={tenantResult} onDismiss={() => setTenantResult(null)} />
      </div>

      {/* ── 5. O365 ACTIONS ──────────────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}><M365Logo /> Microsoft 365 Actions</div>
        <p style={{ margin:"0 0 14px", fontSize:13, color:"var(--t-text-label)", lineHeight:1.6 }}>
          Run scripts and reports against this client's Microsoft 365 tenant.
          Tenant ID must be saved above before running actions.
        </p>

        {!client.azure_tenant_id && (
          <div style={{ background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.4)", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:13, color:"var(--badge-amber)" }}>
            ⚠ Save the Azure Tenant ID above before running M365 actions.
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:10 }}>
          {O365_ACTIONS.map(action => (
            <div key={action.key} style={{
              background:"var(--t-surface-mid)", border:`1px solid ${action.danger?"rgba(220,38,38,0.3)":"var(--t-border-strong)"}`,
              borderRadius:8, padding:"14px 16px",
              opacity:!client.azure_tenant_id ? 0.5 : 1,
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:20 }}>{action.icon}</span>
                  <span style={{ fontSize:14, fontWeight:600, color:"var(--t-text-primary)" }}>{action.label}</span>
                </div>
                {action.danger && <span style={S.sbadge("#ef4444")}>Destructive</span>}
              </div>
              <p style={{ fontSize:12, color:"var(--t-text-label)", margin:"0 0 12px", lineHeight:1.5 }}>{action.description}</p>
              <button
                onClick={() => action.danger ? setConfirmAction(action) : runO365Action(action)}
                disabled={!client.azure_tenant_id || runningAction === action.key}
                style={action.danger ? S.btnRed : S.btnSm}
              >
                {runningAction === action.key ? (
                  <><span style={{ display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span> Running…</>
                ) : `▶ Run ${action.label}`}
              </button>
            </div>
          ))}
        </div>

        <ResultBanner result={actionResult} onDismiss={() => setActionResult(null)} />
      </div>

      {/* Confirm destructive action */}
      {confirmAction && (
        <ConfirmDialog
          message={`Run "${confirmAction.label}"?`}
          subtext={`${confirmAction.description} This action cannot be undone.`}
          confirmLabel={`Run ${confirmAction.label}`}
          danger
          onConfirm={() => runO365Action(confirmAction)}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function Client360() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const isMobile   = useIsMobile();
  const [client,   setClient]   = useState(null);
  const [devices,  setDevices]  = useState([]);
  const [tickets,  setTickets]  = useState([]);
  const [analysis, setAnalysis] = useState("");
  const [loading,  setLoading]  = useState(true);
  const [loadError,setLoadError]= useState("");
  const [analyzing,setAnalyzing]= useState(false);
  const [contacts, setContacts] = useState([]);
  const [tab,      setTab]      = useState("devices");
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeResult,  setWelcomeResult]  = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setLoadError("");
      const [c, d, t, con] = await Promise.all([
        getClient(id), getClientDevices(id), getClientTickets(id), getContacts(id),
      ]);
      const clientObj = normalizeObj(c);
      if (!clientObj?.id) { setLoadError("Client not found."); return; }
      setClient(clientObj);
      setDevices(normalizeArray(d));
      setTickets(normalizeArray(t));
      setContacts(normalizeArray(con));
    } catch (err) {
      setLoadError(`Failed to load: ${err.message}`);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAnalyze = async () => {
    setAnalyzing(true); setAnalysis("");
    try {
      const res = await analyzeClient(id);
      setAnalysis(normalizeObj(res)?.analysis || "No analysis returned.");
    } catch { setAnalysis("Analysis failed. Please try again."); }
    finally { setAnalyzing(false); }
  };

  const eligibleContacts = contacts.filter(c => c.email);

  const handleSendWelcome = async () => {
    const count = eligibleContacts.length;
    if (!count) {
      setWelcomeResult({ type: "error", message: "No contacts with email addresses to send to." });
      return;
    }
    if (!window.confirm(`Send welcome email with portal setup link to ${count} contact${count === 1 ? "" : "s"}? Each recipient will get a 24-hour link to set their password.`)) return;
    setWelcomeSending(true); setWelcomeResult(null);
    try {
      const res = normalizeObj(await sendClientWelcomeEmails(id));
      setWelcomeResult({
        type: res.failed?.length ? "warning" : "success",
        message: `Sent ${res.sent} welcome email${res.sent === 1 ? "" : "s"}${res.failed?.length ? ` — ${res.failed.length} failed` : ""}.`,
      });
    } catch (err) {
      setWelcomeResult({ type: "error", message: err?.response?.data?.error || err.message || "Failed to send welcome emails." });
    } finally { setWelcomeSending(false); }
  };

  if (loading) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12 }}>
      <div style={{ width:36, height:36, border:`3px solid ${tokens.accent.indigo}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <div style={{ fontSize:12, color:tokens.text.faint, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>Loading client…</div>
    </div>
  );

  if (loadError || !client) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ ...S.card, padding:32, textAlign:"center", maxWidth:480 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🏢</div>
        <h2 style={{ fontSize:18, fontWeight:800, color:tokens.text.primary, marginBottom:8, letterSpacing:"-0.3px" }}>Could not load client</h2>
        <p style={{ fontSize:13, color:tokens.text.faint, lineHeight:1.6, marginBottom:16 }}>
          {loadError || "This client couldn't be loaded. They may have been deleted or you don't have access."}
        </p>
        <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
          <button onClick={load} style={sx.buttonGhost}>↺ Retry</button>
          <button onClick={() => navigate("/clients")} style={sx.buttonPrimary}>← Back to Clients</button>
        </div>
      </div>
    </div>
  );

  const alerts      = devices.flatMap(d => (d.alerts||[]).map(a => ({ device:d.device_name, msg:a })));
  const critDevices = devices.filter(d => d.health_score !== null && d.health_score < 60);

  const TABS = [
    { key:"devices", label:`Devices (${devices.length})` },
    { key:"users",   label:`Users (${contacts.length})` },
    { key:"tickets", label:`Tickets (${tickets.length})` },
    { key:"settings",label:"⚙️ Settings" },
  ];

  return (
    <div style={{ ...S.page, ...(isMobile ? { padding: 14 } : {}) }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <button style={S.back} onClick={() => navigate("/clients")}>← Back to Clients</button>

      {/* Header */}
      <div style={S.header}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{
            width:48, height:48, borderRadius:12, flexShrink:0,
            background: tokens.gradient.primary,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:18, fontWeight:800, color:"#fff",
            boxShadow:"0 0 18px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.15)",
          }}>
            {(client.company_name || "?").slice(0,2).toUpperCase()}
          </div>
          <div>
            <h1 style={S.title}>{client.company_name}</h1>
            <p style={S.sub}>
              {client.domain && <span>{client.domain} · </span>}
              {client.city && <span>{client.city}, {client.state} · </span>}
              SLA: {client.sla_policy_name || "None"}
            </p>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <button
            style={S.btn("#0ea5e9")}
            onClick={handleSendWelcome}
            disabled={welcomeSending || eligibleContacts.length === 0}
            title={eligibleContacts.length === 0 ? "No contacts with email addresses" : `Email ${eligibleContacts.length} contact${eligibleContacts.length === 1 ? "" : "s"}`}
          >
            {welcomeSending ? <span style={S.spinner}/> : `📧 Send Welcome Emails (${eligibleContacts.length})`}
          </button>
          <button style={S.btn("#7b61ff")} onClick={handleAnalyze} disabled={analyzing}>
            {analyzing ? <span style={S.spinner}/> : "🤖 AI Pattern Analysis"}
          </button>
        </div>
      </div>

      {client.portal_slug && !client.domain && (
        <div style={{
          background:   "rgba(245,158,11,0.1)",
          border:       "1px solid #b45309",
          color:        "#fcd34d",
          padding:      "10px 14px",
          borderRadius: 8,
          marginBottom: 16,
          fontSize:     13,
          display:      "flex",
          alignItems:   "center",
          gap:          10,
        }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <span>
            <strong>SSO won't work for this portal yet.</strong> This client has a portal ({client.portal_slug}) but no email domain set —
            Microsoft SSO maps users to clients by their email domain. Set the domain in the <em>Domain</em> section below to enable M365 sign-in.
          </span>
        </div>
      )}

      {welcomeResult && (
        <div style={{
          background: welcomeResult.type === "error" ? "rgba(220,38,38,0.1)" : welcomeResult.type === "warning" ? "rgba(245,158,11,0.1)" : "rgba(34,197,94,0.1)",
          border:     `1px solid ${welcomeResult.type === "error" ? "#7f1d1d" : welcomeResult.type === "warning" ? "#b45309" : "#166534"}`,
          color:      welcomeResult.type === "error" ? "#fca5a5" : welcomeResult.type === "warning" ? "#fcd34d" : "#86efac",
          padding:    "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          display:    "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span>{welcomeResult.message}</span>
          <button onClick={() => setWelcomeResult(null)} style={{ background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
        </div>
      )}

      {/* Stat cards */}
      <div style={S.grid}>
        {[
          { val:client.open_tickets  ?? 0, lbl:"Open Tickets" },
          { val:client.total_tickets ?? 0, lbl:"Total Tickets" },
          { val:client.device_count  ?? 0, lbl:"Devices" },
          { val:critDevices.length,         lbl:"Critical Devices", color:critDevices.length>0?"#ef4444":"#22c55e" },
          { val:alerts.length,              lbl:"Active Alerts",    color:alerts.length>0?"#f59e0b":"#22c55e" },
        ].map((s,i) => (
          <div key={i} style={S.stat}>
            <div style={{ ...S.statVal, ...(s.color?{color:s.color}:{}) }}>{s.val}</div>
            <div style={S.statLbl}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Device alerts */}
      {alerts.length > 0 && (
        <div style={S.card}>
          <div style={S.cardH}>⚠️ Device Alerts</div>
          {alerts.map((a,i) => <div key={i} style={S.alert}><strong>{a.device}:</strong> {a.msg}</div>)}
        </div>
      )}

      {/* AI Analysis */}
      {(analysis || analyzing) && (
        <div style={S.card}>
          <div style={S.cardH}>🤖 Claude AI Analysis</div>
          {analyzing
            ? <div style={{ display:"flex", alignItems:"center", gap:10, color:"#6b7280" }}><div style={S.spinner}/> Analyzing patterns...</div>
            : <div style={S.ai}>{analysis}</div>
          }
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:isMobile?"wrap":"nowrap" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            ...S.btn(tab===t.key ? "#4f6fff" : "#1e293b"),
            ...(t.key==="settings" && tab!==t.key ? { border:"1px solid var(--t-border-strong)" } : {}),
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Devices Tab ─────────────────────────────────────────────────── */}
      {tab === "devices" && (
        <div style={S.card}>
          <div style={S.cardH}>Devices</div>
          {devices.length === 0
            ? <p style={{ color:"#6b7280", fontSize:13 }}>No devices registered for this client.</p>
            : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
              <table style={S.table}>
                <thead>
                  <tr>{["Device","OS","Health","CPU","RAM","Disk","Assigned To","AV","Updates","Last Seen",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {devices.map(d => (
                    <tr key={d.device_id}>
                      <td style={S.td}><div style={{ fontWeight:500 }}>{d.device_name}</div><div style={{ fontSize:11, color:"#6b7280" }}>{d.hostname}</div></td>
                      <td style={S.td}><div style={{ fontSize:12 }}>{d.os}</div><div style={{ fontSize:11, color:"#6b7280" }}>{d.os_version}</div></td>
                      <td style={S.td}>
                        {d.health_score !== null ? (
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={S.score(d.health_score)}>{d.health_score}</div>
                            <span style={{ fontSize:11, color:scoreColor(d.health_score) }}>{scoreLabel(d.health_score)}</span>
                          </div>
                        ) : <span style={{ color:"#6b7280" }}>—</span>}
                      </td>
                      <td style={S.td}>{d.cpu_load_percent  != null ? `${d.cpu_load_percent}%`  : "—"}</td>
                      <td style={S.td}>{d.ram_used_percent  != null ? `${d.ram_used_percent}%`  : "—"}</td>
                      <td style={S.td}><span style={{ color:d.disk_used_percent>90?"#ef4444":d.disk_used_percent>80?"#f59e0b":"var(--t-text-body)" }}>{d.disk_used_percent != null ? `${d.disk_used_percent}%` : "—"}</span></td>
                      <td style={S.td}>
                        <select
                          value={d.contact_id || ""}
                          onChange={async e => {
                            try { await updateDevice(d.device_id, { contact_id:e.target.value||null }); await load(); }
                            catch { alert("Failed to assign contact"); }
                          }}
                          style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:6, padding:"4px 8px", color:"var(--t-text-primary)", fontSize:12, cursor:"pointer", maxWidth:150 }}
                        >
                          <option value="">Unassigned</option>
                          {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                        </select>
                      </td>
                      <td style={S.td}><span style={S.badge(d.av_status==="enabled"?"#22c55e":"#ef4444")}>{d.av_status||"—"}</span></td>
                      <td style={S.td}><span style={{ color:d.pending_updates>10?"#f59e0b":"var(--t-text-body)" }}>{d.pending_updates??""}</span></td>
                      <td style={S.td} title={fmtTime(d.last_seen)}>{fmt(d.last_seen)}</td>
                      <td style={S.td}>
                        <RemoteButton deviceId={d.device_id} deviceName={d.device_name} contactName={client.company_name} size="sm" variant="outline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Users Tab (active contacts) ─────────────────────────────────── */}
      {tab === "users" && (
        <div style={S.card}>
          <div style={S.cardH}>Active Users</div>
          {contacts.length === 0
            ? <p style={{ color:"#6b7280", fontSize:13 }}>No active users for this client.</p>
            : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
              <table style={S.table}>
                <thead>
                  <tr>{["Name","Title","Email","Phone","Portal Access",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {contacts.map(c => {
                    const fullName = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
                    const role = c.portal_role || (c.portal_enabled ? "user" : null);
                    return (
                      <tr key={c.id} style={{ cursor:"pointer" }} onClick={() => navigate(`/contacts/${c.id}`)}>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div style={{
                              width:30, height:30, borderRadius:"50%", flexShrink:0,
                              background: tokens.gradient.primary, color:"#fff",
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:11, fontWeight:700,
                              boxShadow:"0 0 12px rgba(79,111,255,0.35)",
                            }}>
                              {fullName.slice(0,2).toUpperCase()}
                            </div>
                            <Link to={`/contacts/${c.id}`} onClick={e => e.stopPropagation()}
                              style={{ fontWeight:600, color:"var(--accent-blue, #5b8cff)", textDecoration:"none" }}>
                              {fullName}
                            </Link>
                          </div>
                        </td>
                        <td style={S.td}>{c.job_title || "—"}</td>
                        <td style={S.td}>{c.email ? <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} style={{ color:"var(--t-text-body)", textDecoration:"none" }}>{c.email}</a> : "—"}</td>
                        <td style={S.td}>{c.phone || "—"}</td>
                        <td style={S.td}>{role ? <span style={S.badge(role==="admin" ? "#7b61ff" : "#22c55e")}>{role}</span> : <span style={{ color:"#6b7280" }}>No portal</span>}</td>
                        <td style={S.td}><span style={{ color:"var(--accent-blue, #5b8cff)", fontSize:12, fontWeight:600 }}>View →</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Tickets Tab ─────────────────────────────────────────────────── */}
      {tab === "tickets" && (
        <div style={S.card}>
          <div style={S.cardH}>
            Recent Tickets
            <Link to={`/tickets?client_id=${id}`} style={{ fontSize:12, color:"#3b82f6", textDecoration:"none" }}>View all →</Link>
          </div>
          {tickets.length === 0
            ? <p style={{ color:"#6b7280", fontSize:13 }}>No tickets found for this client.</p>
            : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
              <table style={S.table}>
                <thead>
                  <tr>{["Ticket","Subject","Submitter","Status","Priority","SLA","Date"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} style={{ cursor:"pointer" }} onClick={() => navigate(`/tickets/${t.id}`)}>
                      <td style={S.td}><span style={{ color:"#3b82f6", fontWeight:500 }}>{t.ticket_number}</span></td>
                      <td style={{ ...S.td, maxWidth:220 }}>
                        <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.subject}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{t.category}</div>
                      </td>
                      <td style={S.td}>{t.submitter_name||"—"}</td>
                      <td style={S.td}><span style={S.badge(statusColor(t.status))}>{t.status}</span></td>
                      <td style={S.td}><span style={S.badge(priorityColor(t.priority))}>{t.priority}</span></td>
                      <td style={S.td}><span style={S.badge(slaColor(t.sla_status))}>{slaLabel(t.sla_status)}</span></td>
                      <td style={S.td}>{fmt(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Settings Tab ────────────────────────────────────────────────── */}
      {tab === "settings" && (
        <ClientSettingsTab client={client} onUpdate={load} />
      )}
    </div>
  );
}
